'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { recordProductEvent } from '@/lib/product-analytics';
import { pageArea, scrollBucket, visibleTimeBucket } from '@/lib/visitor-journey';

export function VisitorJourney({ initializeAnalytics }: { initializeAnalytics?: () => void } = {}) {
  const pathname = usePathname();
  useEffect(() => {
    // Initialize the dispatcher before the first view, including child-first effects.
    initializeAnalytics?.();
    const area = pageArea(pathname);
    if (!area) return;
    const context = { page_area: area };
    recordProductEvent('feature_viewed', context);
    let visibleSince: number | null = document.hidden ? null : performance.now();
    let visibleMs = 0;
    let maxScroll = 0;
    let engaged = false;
    let finished = false;
    const visibleSeconds = () => (visibleMs + (visibleSince === null ? 0 : performance.now() - visibleSince)) / 1000;
    const checkEngaged = () => {
      if (!engaged && visibleSeconds() >= 30) {
        engaged = true;
        recordProductEvent('feature_engaged', context);
      }
    };
    const onVisibility = () => {
      if (document.hidden && visibleSince !== null) {
        visibleMs += performance.now() - visibleSince;
        visibleSince = null;
      } else if (!document.hidden && visibleSince === null) visibleSince = performance.now();
      checkEngaged();
    };
    const onScroll = () => {
      const depth = scrollBucket(window.scrollY, document.documentElement.scrollHeight, window.innerHeight);
      if (depth > maxScroll) {
        maxScroll = depth;
        recordProductEvent('content_depth_reached', { ...context, scroll_percent: depth });
      }
    };
    const onClick = (event: MouseEvent) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(link instanceof HTMLAnchorElement)) return;
      try {
        const target = new URL(link.href);
        if (target.origin !== window.location.origin) return;
        const destination = pageArea(target.pathname);
        if (destination && destination !== area) recordProductEvent('feature_link_clicked', {
          ...context, destination_area: destination,
          placement: link.closest('header, nav') ? 'navigation' : link.closest('footer') ? 'footer' : 'content',
        });
      } catch { /* Ignore malformed links. */ }
    };
    const finish = () => {
      if (finished) return;
      finished = true;
      checkEngaged();
      recordProductEvent('page_visit_ended', { ...context, visible_time_bucket: visibleTimeBucket(visibleSeconds()), scroll_percent: maxScroll });
    };
    // A bfcache restore starts a new observed visit, with listeners still mounted.
    const restore = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      finished = false; engaged = false; visibleMs = 0; maxScroll = 0;
      visibleSince = document.hidden ? null : performance.now();
      recordProductEvent('feature_viewed', context);
    };
    const timer = window.setInterval(checkEngaged, 5000);
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('click', onClick);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', finish);
    window.addEventListener('pageshow', restore);
    return () => {
      finish();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('click', onClick);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', finish);
      window.removeEventListener('pageshow', restore);
    };
  }, [pathname, initializeAnalytics]);
  return null;
}
